import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Star toggle button for favoriting fields and tasks.
 *
 * Controlled component: parent owns the isFavorite state.
 * On click: calls onToggle(newState). Parent must:
 *   - Update state optimistically
 *   - Call API (toggleFavorite)
 *   - Revert state on error
 */
export default function FavoriteButton({
  isFavorite = false,
  onToggle,
  isLoading = false,
  size = 'md',
  className = '',
}) {
  const { t } = useTranslation();

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (isLoading || !onToggle) return;
    onToggle(!isFavorite);
  }, [isFavorite, isLoading, onToggle]);

  const sizeClass = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' }[size] || 'w-5 h-5';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      aria-label={isFavorite
        ? t('favorites.removeFromFavorites', 'Aus Favoriten entfernen')
        : t('favorites.addToFavorites', 'Zu Favoriten hinzufügen')}
      aria-pressed={isFavorite}
      data-testid="favorite-button"
      className={`inline-flex items-center justify-center p-1 rounded-md transition-colors
        focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]
        hover:bg-[var(--aif-bg-tertiary)] disabled:opacity-50 ${className}`}
    >
      <svg
        viewBox="0 0 24 24"
        className={`${sizeClass} transition-colors ${
          isFavorite
            ? 'fill-[var(--aif-color-warning)] stroke-[var(--aif-color-warning)]'
            : 'fill-none stroke-[var(--aif-text-muted)] hover:stroke-[var(--aif-color-warning)]'
        }`}
        strokeWidth="1.5"
      >
        <path
          d="M12 2.5l2.93 6.04 6.66.97-4.81 4.69 1.13 6.62L12 17.69l-5.91 3.13 1.13-6.62L2.41 9.51l6.66-.97L12 2.5z"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
